import React from 'react';
import PropTypes from 'prop-types';
import { map, sortBy } from 'lodash';
import pluralize from 'pluralize';

import { Dropdown, MenuItem } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons';

// NOT exported
const minRecordsForMerge = 2;
const maxRecordsForMerge = 5;

export default class ActionsDropdown extends React.Component {
  static propTypes = {
    aggregateView: PropTypes.bool,
    callLists: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })),
    callListsEnabled: PropTypes.bool,
    canAccessCrm: PropTypes.bool,
    canDelete: PropTypes.bool,
    canFindDuplicates: PropTypes.bool,
    canManageTags: PropTypes.bool,
    canTransferRecords: PropTypes.bool,
    canViewProjects: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })).isRequired,
    isCompany: PropTypes.bool,
    isPerson: PropTypes.bool,
    isProperty: PropTypes.bool,
    selectedGroupIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    selectedIds: PropTypes.array,
    onAddLeadsToProjects: PropTypes.func,
    onAddToCallList: PropTypes.func,
    onAddToGroup: PropTypes.func.isRequired,
    onBulkDelete: PropTypes.func,
    onBulkTagging: PropTypes.func.isRequired,
    onCopyRecords: PropTypes.func,
    onCreateCallList: PropTypes.func,
    onCreateGroup: PropTypes.func.isRequired,
    onCreateTasks: PropTypes.func,
    onDeleteFromGroup: PropTypes.func,
    onDeleteGroup: PropTypes.func,
    onEmailToGroup: PropTypes.func,
    onFindDuplicates: PropTypes.func,
    onMergeRecords: PropTypes.func,
    onMoveRecords: PropTypes.func,
    onSeeRelatedContacts: PropTypes.func,
    onUpdateGroup: PropTypes.func
  };

  static defaultProps = {
    callLists: [],
    canViewProjects: false,
    disabled: false,
    selectedGroupIds: []
  };

  static get MAX_RECORDS_FOR_MERGE() {
    return maxRecordsForMerge;
  }

  static get MIN_RECORDS_FOR_MERGE() {
    return minRecordsForMerge;
  }

  static MIN_RECORDS_FOR_COPY = 1;
  static MIN_RECORDS_FOR_MOVE = 1;

  render() {
    const {
      className,
      disabled,
      selectedIds
    } = this.props;

    const dropdownProps = { className, disabled };

    return (
      <Dropdown className="btn btn-default actions-dropdown" id="actions-dropdown" {...dropdownProps}>
        <Dropdown.Toggle>
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {this.renderGroupMenuSegment()}
          {this.renderFindDuplicateContacts()}
          {selectedIds.length > 0 && this.renderSelectedIdsOptions()}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  renderFindDuplicateContacts() {
    const { canFindDuplicates, onFindDuplicates } = this.props;
    if (!canFindDuplicates) return null;

    return (
      <React.Fragment>
        <MenuItem name="find-duplicates" onClick={onFindDuplicates}>Find Duplicates</MenuItem>
        <MenuItem divider />
      </React.Fragment>
    );
  }

  renderGroupMenuSegment() {
    const { aggregateView, canAccessCrm, selectedGroupIds, onDeleteGroup, onUpdateGroup } = this.props;
    const groupLabel = selectedGroupIds && selectedGroupIds.length === 1 ? 'Group' : 'Groups';

    if (!canAccessCrm || aggregateView) return null;

    return (
      <React.Fragment>
        {selectedGroupIds.length && selectedGroupIds[0] !== 'ungrouped' ?
          (
            <React.Fragment>
              <MenuItem name="delete-group" onClick={onDeleteGroup}>Delete {groupLabel}</MenuItem>
              <MenuItem name="rename-group" onClick={onUpdateGroup}>Rename {groupLabel}</MenuItem>
              <MenuItem divider />
            </React.Fragment>
          )
          : this.renderGroupOptions()
        }
        {this.renderAddToGroupSection()}
        {this.renderDeleteFromGroupSection()}
        <MenuItem divider />
      </React.Fragment>
    );
  }

  renderSelectedIdsOptions() {
    const {
      aggregateView,
      callListsEnabled,
      canAccessCrm,
      canDelete,
      canManageTags,
      canTransferRecords,
      isCompany,
      isPerson,
      isProperty,
      selectedIds,
      onBulkDelete,
      onBulkTagging,
      onCopyRecords,
      onMergeRecords,
      onMoveRecords,
      onSeeRelatedContacts
    } = this.props;

    const canMerge = (
      !aggregateView && onMergeRecords && (
        selectedIds.length >= ActionsDropdown.MIN_RECORDS_FOR_MERGE &&
        selectedIds.length <= ActionsDropdown.MAX_RECORDS_FOR_MERGE
      )
    );

    const canCopy = isProperty && !aggregateView && onCopyRecords &&
                    selectedIds.length >= ActionsDropdown.MIN_RECORDS_FOR_COPY;
    const canMove = isProperty && !aggregateView && onMoveRecords &&
                    selectedIds.length >= ActionsDropdown.MIN_RECORDS_FOR_MOVE;
    const canPerformDatabaseAction = canCopy || canMove;

    return (
      <React.Fragment>
        {
          ((isProperty || isCompany) && !aggregateView) &&
          <React.Fragment>
            <MenuItem key="related-contacts" onClick={onSeeRelatedContacts}>See Related Contacts</MenuItem>
            <MenuItem divider />
          </React.Fragment>
        }
        {isPerson && this.renderPeopleOptions()}
        {canAccessCrm && callListsEnabled && !isProperty && this.renderCallListOptions()}
        {canMerge && <MenuItem key="merge-records" name="merge" onClick={onMergeRecords}>Merge</MenuItem>}
        {onBulkTagging && canManageTags &&
          <MenuItem key="bulk-tagging" name="bulk-tagging" onClick={onBulkTagging}>Tag</MenuItem>
        }
        {onBulkDelete && canDelete && (
          <MenuItem key="bulk-delete" name="bulk-delete" onClick={onBulkDelete}>Delete</MenuItem>
        )}
        {
          canPerformDatabaseAction &&
          <React.Fragment>
            <MenuItem divider />
            {
              canMove &&
              <MenuItem
                disabled={!canTransferRecords}
                key="move-records"
                name="move-records"
                onClick={canTransferRecords ? onMoveRecords : null}
              >
                Move to Another Database
              </MenuItem>
            }
            {
              canCopy &&
              <MenuItem
                disabled={!canTransferRecords}
                key="copy-records"
                name="copy-records"
                onClick={canTransferRecords ? onCopyRecords : null}
              >
                Copy to Another Database
              </MenuItem>
            }
          </React.Fragment>
        }
      </React.Fragment>
    );
  }

  renderPeopleOptions() {
    const {
      canAccessCrm,
      canViewProjects,
      selectedIds,
      onAddLeadsToProjects,
      onCreateTasks,
      onEmailToGroup
    } = this.props;

    return (
      <React.Fragment>
        {canAccessCrm && (
          <React.Fragment>
            <MenuItem name="tasks-group" onClick={onCreateTasks}>
              Create {pluralize('Task', selectedIds.length)}
            </MenuItem>
            <MenuItem divider />
            <MenuItem name="email-group" onClick={onEmailToGroup}>Email Contacts</MenuItem>
            <MenuItem divider />
          </React.Fragment>
        )}
        {canViewProjects && (
          <React.Fragment>
            <MenuItem key="projects-group" onClick={onAddLeadsToProjects}>Add Leads to Project</MenuItem>
            <MenuItem divider />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  renderCallListOptions() {
    const { callLists, callListsEnabled, onAddToCallList, onCreateCallList } = this.props;
    if (!callListsEnabled) return null;

    const sortedCallLists = sortBy(callLists, list => list.name);
    const listOptions = map(sortedCallLists, list => (
      <MenuItem
        eventKey={list}
        key={list.id}
        onSelect={onAddToCallList}
      >
        {list.name}
      </MenuItem>
    ));

    const showAddLink = callLists.length > 0;

    return (
      <React.Fragment>
        {onCreateCallList &&
          <MenuItem name="create-call-list" onClick={onCreateCallList}>
            Create Call List
          </MenuItem>
        }
        {showAddLink &&
          <li className="dropdown-submenu to-left">
            <a className="dropdown-item">
              Add to Call List
              <FontAwesomeIcon className="text-muted mt-2 pull-right" icon={faCaretRight} />
            </a>
            <ul className="dropdown-menu">
              {listOptions}
            </ul>
          </li>
        }
        <MenuItem divider />
      </React.Fragment>
    );
  }

  renderGroupOptions() {
    const { selectedIds } = this.props;

    if (selectedIds.length == 0) return;

    return <MenuItem name="create-group" onClick={this.props.onCreateGroup}>Create Group</MenuItem>;
  }

  renderAddToGroupSection() {
    const {
      groups,
      selectedIds,
      onAddToGroup
    } = this.props;

    if (groups && groups.length > 0 && selectedIds.length > 0) {
      const groupOptions = map(groups, group => (
        <MenuItem
          eventKey={group}
          key={group.id}
          onSelect={onAddToGroup}
        >
          {group.name}
        </MenuItem>
      ));

      return (
        <React.Fragment>
          <li className="dropdown-submenu to-left">
            <a className="dropdown-item">
              Add to Group
              <FontAwesomeIcon className="text-muted mt-2 pull-right" icon={faCaretRight} />
            </a>
            <ul className="dropdown-menu">
              {groupOptions}
            </ul>
          </li>
        </React.Fragment>
      );
    }
  }

  renderDeleteGroupOptions() {
    const {
      groups,
      isProperty,
      isCompany,
      selectedIds,
      selectedGroupIds,
      onDeleteFromGroup
    } = this.props;

    const selectedGroups = selectedGroupIds.length > 0
      ? groups.filter(group => selectedGroupIds.includes(group.id))
      : groups;

    return selectedGroups.map((group) => {
      let ids = [];
      if (isProperty) {
        ids = group.propertyIds;
      } else if (isCompany) {
        ids = group.companyIds;
      } else {
        ids = group.personIds;
      }

      if (ids.some(id=> selectedIds.includes(id))) {
        return (
          <MenuItem
            eventKey={group}
            key={group.id}
            onSelect={onDeleteFromGroup}
          >
            {group.name}
          </MenuItem>
        );
      }
    });
  }

  renderDeleteFromGroupSection() {
    const { groups } = this.props;
    if (groups && groups.length > 0) {
      const groupOptions = this.renderDeleteGroupOptions();
      if (groupOptions.filter(Boolean).length > 0) {
        return (
          <React.Fragment>
            <li className="dropdown-submenu to-left" name="delete-from-group">
              <a className="dropdown-item">
                Delete from Group
                <FontAwesomeIcon className="text-muted mt-2 pull-right" icon={faCaretRight} />
              </a>
              <ul className="dropdown-menu">
                {groupOptions}
              </ul>
            </li>
          </React.Fragment>
        );
      }
    }
  }
}
